
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  CalendarIcon,
  UserIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { stripHtml, estimateReadingTime } from '../../services/utils';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';

const ArticleCard = ({ article, size = 'default' }) => {
  const {
    title,
    excerpt,
    slug,
    featuredImage,
    postDate,
    categories,
    author,
    content,
    cleanContent,
  } = article;

  const readingTime = article.readingTime || estimateReadingTime(content || cleanContent || '');
  const formattedDate = postDate
    ? format(new Date(postDate), 'dd MMMM yyyy', { locale: fr })
    : '';

  const cardClass = size === 'large'
    ? 'group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1'
    : 'group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1';

  // Images beaucoup plus grandes
  const imageHeight = size === 'large' ? 'h-80 md:h-96' : 'h-64 md:h-80';

  return (
    <article className={cardClass}>
      <div className="relative overflow-hidden">
        <Link to={`/article/${slug}`}>
          <div className={`relative w-full ${imageHeight}`}>
            {article.image ? (
              <img src={article.image} alt={title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center text-center px-4">
                <span className="text-gray-500 text-sm font-semibold line-clamp-2">
                  {stripHtml(title)}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                <span>Lire</span>
                <ArrowRightIcon className="w-3 h-3" />
              </div>
            </div>
          </div>
        </Link>

        {categories?.length > 0 && (
          <Link
            to={`/category/${categories[0].slug}`}
            className="absolute top-3 left-3 px-2 py-1 bg-gradient-to-r from-blue-800 to-blue-900 text-white text-xs font-semibold rounded-full hover:from-blue-900 hover:to-blue-800 transition-all duration-300 shadow-lg"
          >
            {categories[0].name}
          </Link>
        )}

        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-slate-700 flex items-center space-x-1">
          <ClockIcon className="w-3 h-3" />
          <span>{readingTime} min</span>
          
        </div>
      </div>

      {/* Contenu réduit au minimum */}
      <div className="p-3">
        <h3 className="text-base font-bold text-slate-800 mb-1 line-clamp-2 leading-tight">
          <Link to={`/article/${slug}`} className="hover:text-red-600 transition-colors duration-300">
            {stripHtml(title)}
          </Link>
        </h3>

        <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
          {formattedDate && (
            <div className="flex items-center space-x-1">
              <CalendarIcon className="w-3 h-3 text-blue-700" />
              <span>{formattedDate}</span>
            </div>
          )}
          
          {author && (
            <div className="flex items-center space-x-1">
              <UserIcon className="w-3 h-3 text-red-600" />
              <span className="font-medium">{author.name}</span> 
            </div>
          )}
        </div>

        {/* Bouton "Lire l'article" compact */}
        <Link
          to={`/article/${slug}`}
          className="inline-flex items-center space-x-1 text-xs font-semibold text-blue-800 hover:text-red-600 transition-colors duration-300 group"
        >
          <span>Lire l'article</span>
          <ArrowRightIcon className="w-3 h-3 transform group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </div>

      <div className="h-1 bg-gradient-to-r from-red-600 via-blue-800 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
    </article>
  );
};

export default ArticleCard;